/**
 * Phase 3 Feature M — human-readable rank / feature deltas between KB snapshots.
 */

/**
 * @param {{ diagnosisId?: string, label?: string }[]} prevRanked
 * @param {{ diagnosisId?: string, label?: string }[]} nextRanked
 * @returns {string[]}
 */
export function buildRankChangeReasons(prevRanked, nextRanked) {
  const prevMap = new Map();
  (prevRanked || []).forEach((row, i) => {
    const id = String(row.diagnosisId || row.label || '').trim();
    if (id) prevMap.set(id, i + 1);
  });
  const out = [];
  (nextRanked || []).forEach((row, i) => {
    const id = String(row.diagnosisId || row.label || '').trim();
    if (!id) return;
    const label = row.label || id;
    const newR = i + 1;
    const oldR = prevMap.get(id);
    if (oldR == null) {
      out.push(`${label} entered the ranked list at #${newR}.`);
    } else if (oldR !== newR) {
      out.push(`${label} moved from #${oldR} to #${newR}.`);
    }
  });
  for (const [id, oldR] of prevMap) {
    const still = (nextRanked || []).some(
      (row, j) => String(row.diagnosisId || row.label || '').trim() === id
    );
    if (!still) {
      out.push(`${id} dropped out of the ranked list (was #${oldR}).`);
    }
  }
  return out;
}

/**
 * @param {{ diagnosisId: string, matchedFeatures?: { featureId?: string }[] }[]} prevCands
 * @param {{ diagnosisId: string, matchedFeatures?: { featureId?: string }[] }[]} nextCands
 * @returns {string[]}
 */
export function buildFeatureDeltaReasons(prevCands, nextCands) {
  const prevByDx = new Map((prevCands || []).map((c) => [c.diagnosisId, c]));
  const out = [];
  for (const next of nextCands || []) {
    const dxId = next.diagnosisId;
    if (!dxId) continue;
    const prev = prevByDx.get(dxId);
    if (!prev) continue;
    const prevFeat = new Set((prev.matchedFeatures || []).map((m) => m.featureId).filter(Boolean));
    const nextFeat = new Set((next.matchedFeatures || []).map((m) => m.featureId).filter(Boolean));
    for (const fid of nextFeat) {
      if (!prevFeat.has(fid)) {
        out.push(`${dxId}: new supporting feature ${fid}.`);
      }
    }
  }
  return out;
}
